import { useCallback, useEffect, useState } from "react";

import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";

import { format } from "date-fns";

import StatusTransitionCell from "components/status-transition-cell";
import type { StatusTransitionCode } from "components/status-transition-cell/status-transition-cell.component";
import {
  TASK_STATUSES,
  TASK_STATUS_COLORS,
  TASK_STATUS_TODO
} from "constants/staff-tasks";

type TaskStatusFieldProps = {
  completedAtDate?: string | null;
  value: number | string | undefined;
  onChange?: (code: StatusTransitionCode) => Promise<any>;
};

export default function TaskStatusField({
  completedAtDate,
  value,
  onChange
}: TaskStatusFieldProps) {
  const [optimisticStatus, setOptimisticStatus] = useState(
    value || TASK_STATUS_TODO
  );

  useEffect(() => {
    setOptimisticStatus(value || TASK_STATUS_TODO);
  }, [value]);

  const handleChange = useCallback(
    (code: StatusTransitionCode) => {
      setOptimisticStatus(Number(code));
      if (onChange) {
        return onChange(code);
      }
      return new Promise((resolve, reject) => {
        resolve(code);
      });
    },
    [onChange]
  );

  return (
    <Stack rowGap={0.7}>
      <StatusTransitionCell
        status={optimisticStatus as StatusTransitionCode}
        statusColors={TASK_STATUS_COLORS}
        allStatuses={TASK_STATUSES}
        readonly={!Boolean(onChange)}
        isSpaceBetween
        onChange={handleChange}
        isNoWrap
      />
      {completedAtDate && (
        <Tooltip
          title={`Completed at ${format(
            new Date(completedAtDate),
            "EEE MMM dd, yyyy hh:mm aaa"
          )}`}
          disableInteractive
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              lineHeight: "1.0625rem",
              ml: 2,
              color: grey[700]
            }}
          >
            {format(new Date(completedAtDate), "EEE MMM dd")}
          </Typography>
        </Tooltip>
      )}
    </Stack>
  );
}
